import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import InspectionsPage from './pages/InspectionsPage';
import InspectionDetailPage from './pages/InspectionDetailPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import ProfilePage from './pages/ProfilePage';
import Verify2FAPage from './pages/Verify2FAPage';
import AdminLookupsPage from './pages/AdminLookupsPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import RoleManagementPage from './pages/admin/RoleManagementPage';
import FirmSettingsPage from './pages/admin/FirmSettingsPage';
import DevelopmentAppraisalsPage from './pages/DevelopmentAppraisalsPage';
import DevelopmentAppraisalDetailPage from './pages/DevelopmentAppraisalDetailPage';
import ClientReviewPortal from './components/ClientReviewPortal';
import ReviewDashboardPage from './pages/admin/ReviewDashboardPage';
import ReverseEngineeringPage from './pages/ReverseEngineeringPage';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function ProtectedRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        await fetchUser(storedToken);
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const fetchUser = async (authToken = token) => {
    if (!authToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      setToken(authToken);
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    await fetchUser(newToken);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('temp_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, fetchUser }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-2fa" element={<Verify2FAPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections"
              element={
                <ProtectedRoute>
                  <InspectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspections/:id"
              element={
                <ProtectedRoute>
                  <InspectionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <ProtectedRoute>
                  <ReportDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute>
                  <RoleManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lookups"
              element={
                <ProtectedRoute>
                  <AdminLookupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/firm-settings"
              element={
                <ProtectedRoute>
                  <FirmSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-appraisals"
              element={
                <ProtectedRoute>
                  <DevelopmentAppraisalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/development-appraisals/:id"
              element={
                <ProtectedRoute>
                  <DevelopmentAppraisalDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <ProtectedRoute>
                  <ReviewDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reverse-engineering"
              element={
                <ProtectedRoute>
                  <ReverseEngineeringPage />
                </ProtectedRoute>
              }
            />
            {/* Public Client Portal - No auth required */}
            <Route path="/client-portal/:reportId" element={<ClientReviewPortal />} />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
